/**
 * useSidebarWorkerExpansion — per-master-thread worker-list expand/collapse state.
 *
 * Tracks which master thread keys have their worker sub-rows expanded. Pure
 * local state with immutable Set updates — mirrors the shape of
 * {@link useSidebarThreadListExpansion} so consumers can treat them uniformly.
 *
 * The pure reducer helpers (applyExpand/applyCollapse/applyToggle) are
 * exported separately so they can be unit-tested without mounting React.
 */

import { useCallback, useState } from "react";

// ---------------------------------------------------------------------------
// Pure reducer helpers (exported for testing)
// ---------------------------------------------------------------------------

export function applyExpandWorkers(
  current: ReadonlySet<string>,
  threadKey: string,
): ReadonlySet<string> {
  if (current.has(threadKey)) return current;
  const next = new Set(current);
  next.add(threadKey);
  return next;
}

export function applyCollapseWorkers(
  current: ReadonlySet<string>,
  threadKey: string,
): ReadonlySet<string> {
  if (!current.has(threadKey)) return current;
  const next = new Set(current);
  next.delete(threadKey);
  return next;
}

export function applyToggleWorkers(
  current: ReadonlySet<string>,
  threadKey: string,
): ReadonlySet<string> {
  const next = new Set(current);
  if (current.has(threadKey)) {
    next.delete(threadKey);
  } else {
    next.add(threadKey);
  }
  return next;
}

// ---------------------------------------------------------------------------
// Return type
// ---------------------------------------------------------------------------

export interface SidebarWorkerExpansionResult {
  readonly expandedWorkersByThread: ReadonlySet<string>;
  readonly expandWorkersForThread: (threadKey: string) => void;
  readonly collapseWorkersForThread: (threadKey: string) => void;
  readonly toggleWorkersForThread: (threadKey: string) => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSidebarWorkerExpansion(): SidebarWorkerExpansionResult {
  const [expandedWorkersByThread, setExpanded] = useState<ReadonlySet<string>>(() => new Set());

  const expandWorkersForThread = useCallback((threadKey: string) => {
    setExpanded((current) => applyExpandWorkers(current, threadKey));
  }, []);

  const collapseWorkersForThread = useCallback((threadKey: string) => {
    setExpanded((current) => applyCollapseWorkers(current, threadKey));
  }, []);

  const toggleWorkersForThread = useCallback((threadKey: string) => {
    setExpanded((current) => applyToggleWorkers(current, threadKey));
  }, []);

  return {
    expandedWorkersByThread,
    expandWorkersForThread,
    collapseWorkersForThread,
    toggleWorkersForThread,
  };
}
